import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "../../hooks/useTranslation";
import { useCreateSource, useBulkCreateSources } from "../../hooks/useApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Link as LinkIcon,
  Globe,
  AlertTriangle,
  Info,
  CheckCircle,
  RefreshCw,
  List,
  Upload,
} from "lucide-react";

const UrlSourceDialog = ({ isOpen, onOpenChange, botId, onSuccess }) => {
  const { t } = useTranslation();
  const createSourceMutation = useCreateSource();
  const bulkCreateMutation = useBulkCreateSources();
  const [urlPreview, setUrlPreview] = useState(null);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkValidation, setBulkValidation] = useState({
    valid: [],
    invalid: [],
  });
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Indirizzi rimasti fuori perche' il piano non comprende altre fonti.
  const [oltreIlPiano, setOltreIlPiano] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
  } = useForm({
    defaultValues: {
      url: "",
      refresh_days: 30,
    },
  });

  const watchedUrl = watch("url");
  const watchedRefreshDays = watch("refresh_days");

  // Validate URL format
  const validateUrl = (url) => {
    try {
      const urlObj = new URL(url.trim());
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  // Validate bulk URLs
  const validateBulkUrls = (urlsText) => {
    const lines = urlsText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const valid = [];
    const invalid = [];

    lines.forEach((line) => {
      if (validateUrl(line)) {
        // Check for duplicates
        if (!valid.includes(line)) {
          valid.push(line);
        }
      } else {
        invalid.push(line);
      }
    });

    setBulkValidation({ valid, invalid });
    return { valid, invalid, total: lines.length };
  };

  // Handle bulk URL input change
  React.useEffect(() => {
    if (bulkMode && bulkUrls) {
      const timeoutId = setTimeout(() => {
        validateBulkUrls(bulkUrls);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [bulkUrls, bulkMode]);

  // Preview URL (simulate checking if URL is accessible)
  const previewUrl = async (url) => {
    if (!validateUrl(url)) {
      setError("url", {
        message: t(
          "dialogs.url.validation.invalidUrl",
          "Please enter a valid HTTP or HTTPS URL"
        ),
      });
      return;
    }

    setIsValidatingUrl(true);
    clearErrors("url");

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const urlObj = new URL(url);
      setUrlPreview({
        title: t("dialogs.url.preview.contentFrom", "Content from {{hostname}}", {
          hostname: urlObj.hostname,
        }),
        description: t(
          "dialogs.url.preview.accessibleDescription",
          "This URL appears to be accessible and will be crawled for content."
        ),
        domain: urlObj.hostname,
        protocol: urlObj.protocol,
        accessible: true,
      });
    } catch (error) {
      setUrlPreview({
        accessible: false,
        error: t(
          "dialogs.url.preview.notAccessibleError",
          "Unable to access this URL. Please check the URL and try again."
        ),
      });
    } finally {
      setIsValidatingUrl(false);
    }
  };

  // Handle URL input change with debounced validation
  React.useEffect(() => {
    if (watchedUrl && watchedUrl.length > 10) {
      const timeoutId = setTimeout(() => {
        previewUrl(watchedUrl);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setUrlPreview(null);
    }
  }, [watchedUrl]);

  // Handle form submission (single URL)
  const onSubmit = async (data) => {
    if (!validateUrl(data.url)) {
      setError("url", {
        message: t(
          "dialogs.url.validation.invalidUrl",
          "Please enter a valid HTTP or HTTPS URL"
        ),
      });
      return;
    }

    try {
      await createSourceMutation.mutateAsync({
        botId,
        data: {
          type: "url",
          url: data.url,
          refresh_days: parseInt(data.refresh_days),
        },
      });

      reset();
      setBulkUrls("");
      setBulkValidation({ valid: [], invalid: [] });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create URL source:", error);
    }
  };

  // Handle bulk submission
  const onBulkSubmit = async () => {
    const { valid } = bulkValidation;

    if (valid.length === 0) {
      return;
    }

    setIsProcessingBulk(true);
    setDuplicateWarning(null); // Reset warning
    setOltreIlPiano(null);

    try {
      const refreshDays = parseInt(watchedRefreshDays);

      // Create sources array for bulk API
      const sources = valid.map((url) => ({
        type: "url",
        url: url,
        refresh_days: refreshDays,
      }));

      // Use bulk create API - single request instead of N requests
      const result = await bulkCreateMutation.mutateAsync({
        botId,
        sources,
      });

      // Il server crea quelle che stanno nel piano: le altre si dicono qui.
      if (result.over_limit > 0) {
        setOltreIlPiano({ added: result.count, over: result.over_limit, limit: result.limit });
      }

      // Check if some duplicates were skipped
      if (result.skipped > 0) {
        setDuplicateWarning({
          created: result.count,
          skipped: result.skipped,
          total: result.total_submitted,
        });
      }

      reset();
      setBulkUrls("");
      setBulkValidation({ valid: [], invalid: [] });

      // Don't close immediately if there were duplicates, let user see the warning
      if (result.skipped === 0 && !result.over_limit) {
        onOpenChange(false);
      }

      onSuccess?.();
    } catch (error) {
      console.error("Failed to create bulk URL sources:", error);

      // Handle specific error for all duplicates
      if (error.response?.data?.error === "All sources already exist") {
        setDuplicateWarning({
          created: 0,
          skipped: error.response.data.skipped,
          total: valid.length,
          allDuplicates: true,
        });
      }
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // Sample URLs for demonstration
  const sampleUrls = [
    "https://docs.example.com/getting-started",
    "https://blog.example.com/best-practices",
    "https://help.example.com/faq",
  ];

  const insertSampleUrl = (url) => {
    setValue("url", url);
  };

  // Reset form when dialog closes
  const handleOpenChange = (open) => {
    if (!open) {
      reset();
      setBulkUrls("");
      setBulkValidation({ valid: [], invalid: [] });
      setUrlPreview(null);
      setIsValidatingUrl(false);
      setIsProcessingBulk(false);
      setDuplicateWarning(null);
      setOltreIlPiano(null);
    }
    onOpenChange(open);
  };

  const isSubmitting = createSourceMutation.isPending || isProcessingBulk;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("dialogs.url.title", "Add URL Sources")}</DialogTitle>
          <DialogDescription>
            {t(
              "dialogs.url.description",
              "Add single or multiple web pages to your bot's knowledge base. URLs will be crawled and automatically updated."
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue="single"
          onValueChange={(value) => setBulkMode(value === "bulk")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4" />
              {t("dialogs.url.tabs.single", "Single URL")}
            </TabsTrigger>
            <TabsTrigger value="bulk" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              {t("dialogs.url.tabs.bulk", "Bulk Import")}
            </TabsTrigger>
          </TabsList>

          {/* SINGLE URL TAB */}
          <TabsContent value="single" className="space-y-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* URL Input */}
              <div className="space-y-2">
                <Label htmlFor="url">{t("dialogs.url.urlLabel", "URL")}</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="url"
                    type="url"
                    {...register("url", {
                      required: t(
                        "dialogs.url.validation.urlRequired",
                        "URL is required"
                      ),
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: t(
                          "dialogs.url.validation.invalidUrl",
                          "Please enter a valid HTTP or HTTPS URL"
                        ),
                      },
                    })}
                    placeholder="https://example.com/page"
                    className="pl-10"
                    disabled={isSubmitting}
                  />
                  {isValidatingUrl && (
                    <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                  )}
                </div>
                {errors.url && (
                  <p className="text-sm text-red-600">{errors.url.message}</p>
                )}

                {/* Sample URLs */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-muted-foreground">
                    {t("dialogs.url.tryLabel", "Try:")}
                  </span>
                  {sampleUrls.map((url, index) => (
                    <Button
                      key={index}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => insertSampleUrl(url)}
                      disabled={isSubmitting}
                      className="text-xs h-6"
                    >
                      {new URL(url).hostname}
                    </Button>
                  ))}
                </div>
              </div>

              {/* URL Preview */}
              {urlPreview && (
                <div
                  className={`border rounded-lg p-4 ${
                    urlPreview.accessible
                      ? "border-green-200 bg-green-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {urlPreview.accessible ? (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      {urlPreview.accessible ? (
                        <>
                          <h4 className="text-sm font-medium text-green-800">
                            {t("dialogs.url.preview.accessibleTitle", "URL Accessible")}
                          </h4>
                          <p className="text-sm text-green-700 mt-1">
                            {urlPreview.description}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-green-600">
                            <span>
                              {t("dialogs.url.preview.domain", "Domain:")}{" "}
                              {urlPreview.domain}
                            </span>
                            <span>
                              {t("dialogs.url.preview.protocol", "Protocol:")}{" "}
                              {urlPreview.protocol}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <h4 className="text-sm font-medium text-red-800">
                            {t(
                              "dialogs.url.preview.notAccessibleTitle",
                              "URL Not Accessible"
                            )}
                          </h4>
                          <p className="text-sm text-red-700 mt-1">
                            {urlPreview.error}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Refresh Frequency */}
              <div className="space-y-2">
                <Label htmlFor="refresh_days">
                  {t(
                    "dialogs.crawl.frequencyLabel",
                    "Frequenza di aggiornamento automatico"
                  )}
                </Label>
                <Select
                  value={String(watchedRefreshDays || 30)}
                  onValueChange={(value) =>
                    setValue("refresh_days", parseInt(value))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="refresh_days">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">
                      {t("dialogs.crawl.every5", "Ogni 5 giorni")}
                    </SelectItem>
                    <SelectItem value="10">
                      {t("dialogs.crawl.every10", "Ogni 10 giorni")}
                    </SelectItem>
                    <SelectItem value="30">
                      {t(
                        "dialogs.crawl.every30",
                        "Ogni 30 giorni (consigliato)"
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "dialogs.crawl.help",
                    "Il bot ricontrollerà questa pagina con questa frequenza e aggiornerà automaticamente la sua conoscenza se il contenuto cambia."
                  )}
                </p>
              </div>

              {/* Error Display */}
              {createSourceMutation.error && !bulkMode && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">
                        {createSourceMutation.error.response?.status === 409
                          ? t("dialogs.url.error.duplicateTitle", "Duplicate URL")
                          : t(
                              "dialogs.url.error.createTitle",
                              "Error creating source"
                            )}
                      </p>
                      <p className="text-xs mt-1">
                        {createSourceMutation.error.response?.status === 409
                          ? t(
                              "dialogs.url.error.duplicateMessage",
                              "This URL already exists as a source for this bot."
                            )
                          : createSourceMutation.error.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSubmitting}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? t("dialogs.url.creating", "Creating...")
                    : t("dialogs.url.addUrlSource", "Add URL Source")}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          {/* BULK IMPORT TAB */}
          <TabsContent value="bulk" className="space-y-4">
            <div className="space-y-6">
              {/* Bulk URL Textarea */}
              <div className="space-y-2">
                <Label htmlFor="bulk_urls">
                  {t("dialogs.url.bulk.urlsLabel", "URLs (one per line)")}
                </Label>
                <Textarea
                  id="bulk_urls"
                  value={bulkUrls}
                  onChange={(e) => setBulkUrls(e.target.value)}
                  placeholder="https://example.com/page-1&#10;https://example.com/page-2&#10;https://example.com/page-3&#10;..."
                  className="min-h-[200px] font-mono text-sm"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "dialogs.url.bulk.urlsHelp",
                    "Enter one URL per line. Invalid URLs will be highlighted below."
                  )}
                </p>
              </div>

              {/* Validation Results */}
              {(bulkValidation.valid.length > 0 ||
                bulkValidation.invalid.length > 0) && (
                <div className="space-y-3">
                  {/* Valid URLs */}
                  {bulkValidation.valid.length > 0 && (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-green-800">
                            {bulkValidation.valid.length}{" "}
                            {bulkValidation.valid.length !== 1
                              ? t("dialogs.url.bulk.validUrls", "Valid URLs")
                              : t("dialogs.url.bulk.validUrl", "Valid URL")}
                          </h4>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {bulkValidation.valid
                              .slice(0, 5)
                              .map((url, idx) => (
                                <div
                                  key={idx}
                                  className="text-xs text-green-700 font-mono truncate"
                                >
                                  • {url}
                                </div>
                              ))}
                            {bulkValidation.valid.length > 5 && (
                              <p className="text-xs text-green-600 mt-1">
                                {t(
                                  "dialogs.url.bulk.andMore",
                                  "...and {{count}} more",
                                  { count: bulkValidation.valid.length - 5 }
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Invalid URLs */}
                  {bulkValidation.invalid.length > 0 && (
                    <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-red-800">
                            {bulkValidation.invalid.length}{" "}
                            {bulkValidation.invalid.length !== 1
                              ? t("dialogs.url.bulk.invalidUrls", "Invalid URLs")
                              : t("dialogs.url.bulk.invalidUrl", "Invalid URL")}
                          </h4>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {bulkValidation.invalid.map((url, idx) => (
                              <div
                                key={idx}
                                className="text-xs text-red-700 font-mono truncate"
                              >
                                • {url}
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-red-600 mt-2">
                            {t(
                              "dialogs.url.bulk.willBeSkipped",
                              "These URLs will be skipped"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Refresh Frequency for Bulk */}
              <div className="space-y-2">
                <Label htmlFor="bulk_refresh_days">
                  {t(
                    "dialogs.crawl.frequencyLabelBulk",
                    "Frequenza di aggiornamento automatico (per tutti gli URL)"
                  )}
                </Label>
                <Select
                  value={String(watchedRefreshDays || 30)}
                  onValueChange={(value) =>
                    setValue("refresh_days", parseInt(value))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="bulk_refresh_days">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">
                      {t("dialogs.crawl.every5", "Ogni 5 giorni")}
                    </SelectItem>
                    <SelectItem value="10">
                      {t("dialogs.crawl.every10", "Ogni 10 giorni")}
                    </SelectItem>
                    <SelectItem value="30">
                      {t(
                        "dialogs.crawl.every30",
                        "Ogni 30 giorni (consigliato)"
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "dialogs.crawl.helpBulk",
                    "Questa frequenza verrà applicata a tutti gli URL importati."
                  )}
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-2">
                      {t("dialogs.url.bulk.tipsTitle", "Bulk Import Tips")}
                    </p>
                    <ul className="space-y-1 text-xs">
                      <li>
                        •{" "}
                        {t(
                          "dialogs.url.bulk.tipSingleRequest",
                          "All URLs will be imported in a single request (much faster!)"
                        )}
                      </li>
                      <li>
                        •{" "}
                        {t(
                          "dialogs.url.bulk.tipDuplicates",
                          "Duplicate URLs will be automatically removed"
                        )}
                      </li>
                      <li>
                        •{" "}
                        {t(
                          "dialogs.url.bulk.tipInvalid",
                          "Invalid URLs will be skipped"
                        )}
                      </li>
                      <li>
                        •{" "}
                        {t(
                          "dialogs.url.bulk.tipSameInterval",
                          "All valid URLs will use the same refresh interval"
                        )}
                      </li>
                      <li>
                        •{" "}
                        {t(
                          "dialogs.url.bulk.tipBackground",
                          "Processing happens in the background after import"
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {(createSourceMutation.error || bulkCreateMutation.error) &&
                bulkMode && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">
                        {createSourceMutation.error?.message ||
                          bulkCreateMutation.error?.message ||
                          t(
                            "dialogs.url.bulk.importError",
                            "Failed to import URLs. Please try again."
                          )}
                      </p>
                    </div>
                  </div>
                )}

              {/* Success Display */}
              {bulkCreateMutation.isSuccess && bulkMode && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-700">
                      <p className="font-medium">
                        {t(
                          "dialogs.url.bulk.successTitle",
                          "Successfully imported {{count}} URLs!",
                          { count: bulkCreateMutation.data?.count }
                        )}
                      </p>
                      <p className="text-xs mt-1">
                        {t(
                          "dialogs.url.bulk.successMessage",
                          "Your sources are being processed in the background."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Duplicate Warning Display */}
              {oltreIlPiano && (
                <div className="flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <p>
                    {t("onboardingWizard.sourcesOverPlan", oltreIlPiano)}{" "}
                    <Link to="/billing" className="font-semibold underline underline-offset-2">
                      {t("billing.limitSeePlans")}
                    </Link>
                  </p>
                </div>
              )}
              {duplicateWarning && (
                <div
                  className={`${
                    duplicateWarning.allDuplicates
                      ? "bg-red-50 border-red-200"
                      : "bg-yellow-50 border-yellow-200"
                  } border rounded-lg p-3`}
                >
                  <div className="flex items-start space-x-2">
                    <AlertTriangle
                      className={`h-4 w-4 ${
                        duplicateWarning.allDuplicates
                          ? "text-red-600"
                          : "text-yellow-600"
                      } flex-shrink-0 mt-0.5`}
                    />
                    <div
                      className={`text-sm ${
                        duplicateWarning.allDuplicates
                          ? "text-red-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {duplicateWarning.allDuplicates ? (
                        <>
                          <p className="font-medium">
                            {t(
                              "dialogs.url.duplicate.allTitle",
                              "All URLs already exist!"
                            )}
                          </p>
                          <p className="text-xs mt-1">
                            {t(
                              "dialogs.url.duplicate.allMessage",
                              "All {{count}} URLs you tried to import already exist as sources for this bot.",
                              { count: duplicateWarning.skipped }
                            )}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium">
                            {t(
                              "dialogs.url.duplicate.someTitle",
                              "Some duplicates were skipped"
                            )}
                          </p>
                          <p className="text-xs mt-1">
                            {t(
                              "dialogs.url.duplicate.someMessage",
                              "Created: {{created}} | Skipped: {{skipped}} (already exist) | Total: {{total}}",
                              {
                                created: duplicateWarning.created,
                                skipped: duplicateWarning.skipped,
                                total: duplicateWarning.total,
                              }
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSubmitting}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={onBulkSubmit}
                  disabled={isSubmitting || bulkValidation.valid.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t(
                        "dialogs.url.bulk.importing",
                        "Importing {{count}} URLs...",
                        { count: bulkValidation.valid.length }
                      )}
                    </>
                  ) : bulkValidation.valid.length !== 1 ? (
                    t(
                      "dialogs.url.bulk.importButtonPlural",
                      "Import {{count}} URLs",
                      { count: bulkValidation.valid.length }
                    )
                  ) : (
                    t(
                      "dialogs.url.bulk.importButton",
                      "Import {{count}} URL",
                      { count: bulkValidation.valid.length }
                    )
                  )}
                </Button>
              </DialogFooter>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UrlSourceDialog;
